import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Autocomplete,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import { 
  Category, 
  CheckCircle, 
  ArrowBack,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import GMBService from '../../../services/GMBService';
import logger from '../../../utils/logger';

const HeaderContainer = styled(Box)({
  background: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
  color: 'white',
  padding: '32px 24px',
  textAlign: 'center'
});

const IconContainer = styled(Box)({
  width: '70px',
  height: '70px',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 16px',
  backdropFilter: 'blur(10px)'
});

const ContentContainer = styled(Box)({
  padding: '24px'
});

const ActionButton = styled(Button)(({ variant: buttonVariant }) => ({
  borderRadius: '8px',
  padding: '10px 20px',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  ...(buttonVariant === 'primary' && {
    backgroundColor: '#ff9800',
    color: 'white',
    '&:hover': { backgroundColor: '#f57c00' }
  }),
  ...(buttonVariant === 'secondary' && {
    backgroundColor: '#f5f5f5',
    color: '#333',
    '&:hover': { backgroundColor: '#e0e0e0' }
  })
}));

const CategoryManagementPopup = ({ task, onComplete, onBack, businessProfileData = {}, profileId }) => {
  const [primaryCategory, setPrimaryCategory] = useState(null);
  const [primaryInputValue, setPrimaryInputValue] = useState('');
  const [additionalCategories, setAdditionalCategories] = useState([]);
  const [categorySuggestions, setCategorySuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [searchText, setSearchText] = useState('');
  
  const searchTimer = useRef(null);
  const requestId = useRef(0);

  // Load existing categories from business profile
  useEffect(() => {
    const existingPrimary = businessProfileData?.categories?.primaryCategory;
    if (existingPrimary) {
      setPrimaryCategory({
        id: existingPrimary.categoryId || existingPrimary.name,
        label: existingPrimary.displayName || existingPrimary.name
      });
      setPrimaryInputValue(existingPrimary.displayName || existingPrimary.name || '');
    }

    const existingAdditional = businessProfileData?.categories?.additionalCategories || [];
    if (existingAdditional.length > 0) {
      setAdditionalCategories(existingAdditional.map(cat => ({
        selected: {
          id: cat.categoryId || cat.name,
          label: cat.displayName || cat.name
        },
        inputValue: cat.displayName || cat.name || ''
      })));
    }
  }, [businessProfileData]);

  // Fetch category suggestions from Google API
  const fetchSuggestions = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setCategorySuggestions([]);
      return;
    }
    
    const currentRequestId = ++requestId.current;
    setLoading(true);
    
    try {
      const accessToken = localStorage.getItem('googleAccessToken') || sessionStorage.getItem('googleAccessToken');
      const regionCode = businessProfileData?.storefrontAddress?.regionCode || 'US';
      const languageCode = businessProfileData?.languageCode || 'en';
      
      const response = await GMBService.searchCategoriesWithServices(term, regionCode, languageCode, accessToken);
      
      if (requestId.current !== currentRequestId) return;
      
      const options = response
        .map(cat => ({
          id: cat.categoryId,
          label: cat.displayName
        }))
        .filter(opt => opt.id || opt.label);
      
      setCategorySuggestions(options);
    } catch (err) {
      logger.warn('[CategoryPopup] Error fetching suggestions:', err);
    } finally {
      if (requestId.current === currentRequestId) {
        setLoading(false);
      }
    }
  }, [businessProfileData]);

  // Debounced search
  useEffect(() => {
    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
    }
    
    const trimmed = (searchText || '').trim();
    if (trimmed.length < 2) {
      setCategorySuggestions([]);
      setLoading(false);
      return;
    }
    
    searchTimer.current = setTimeout(() => {
      fetchSuggestions(trimmed);
    }, 400);
    
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchText, fetchSuggestions]);

  // Combine suggestions with current selections for options
  const categoryOptions = useMemo(() => {
    const map = new Map();
    const addOption = (opt) => {
      if (!opt) return;
      const key = opt.id || opt.label;
      if (key && !map.has(key)) {
        map.set(key, { id: opt.id, label: opt.label });
      }
    };
    
    categorySuggestions.forEach(addOption);
    addOption(primaryCategory);
    additionalCategories.forEach(entry => addOption(entry.selected));
    
    return Array.from(map.values());
  }, [categorySuggestions, primaryCategory, additionalCategories]);

  const handlePrimarySelect = (event, newValue) => {
    setPrimaryCategory(newValue);
    setPrimaryInputValue(newValue?.label || '');
    setSearchText('');
    setCategorySuggestions([]);
  };

  const handlePrimaryInputChange = (event, newInputValue, reason) => {
    setPrimaryInputValue(newInputValue);
    if (reason === 'input') {
      setPrimaryCategory(prev => (prev?.label === newInputValue ? prev : null));
      setSearchText(newInputValue);
    } else if (reason === 'clear') {
      setPrimaryCategory(null);
      setSearchText('');
    }
  };

  const handleAdditionalSelect = (index) => (event, newValue) => {
    setAdditionalCategories(prev => prev.map((entry, idx) => {
      if (idx !== index) return entry;
      return {
        selected: newValue ? { id: newValue.id, label: newValue.label } : null,
        inputValue: newValue?.label || ''
      };
    }));
    setSearchText('');
    setCategorySuggestions([]);
  };

  const handleAdditionalInputChange = (index) => (event, newInputValue, reason) => {
    setAdditionalCategories(prev => prev.map((entry, idx) => {
      if (idx !== index) return entry;
      return {
        ...entry,
        inputValue: newInputValue,
        selected: reason === 'input' || reason === 'clear' ? null : entry.selected
      };
    }));
    if (reason === 'input') {
      setSearchText(newInputValue);
    } else if (reason === 'clear') {
      setSearchText('');
    }
  };

  const handleAddCategory = () => {
    setAdditionalCategories(prev => [...prev, { selected: null, inputValue: '' }]);
  };

  const handleRemoveCategory = (index) => {
    setAdditionalCategories(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleComplete = async () => {
    if (!primaryCategory?.id) {
      setError('Please select a primary category');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const accessToken = localStorage.getItem('googleAccessToken') || sessionStorage.getItem('googleAccessToken');
      if (!accessToken) {
        throw new Error('Google authentication required');
      }

      // Build categories payload like BusinessProfile does
      const categoriesPayload = {
        primaryCategory: {
          name: primaryCategory.id,
          displayName: primaryCategory.label
        }
      };

      const validAdditional = additionalCategories.filter(entry => entry.selected?.id);
      if (validAdditional.length > 0) {
        categoriesPayload.additionalCategories = validAdditional.map(entry => ({
          name: entry.selected.id,
          displayName: entry.selected.label
        }));
      }

      // Update directly using GMBService like BusinessProfile does
      await GMBService.updateBusinessProfile(profileId, { categories: categoriesPayload }, accessToken);

      setSuccess(true);
      
      // Complete the task
      setTimeout(() => {
        onComplete({
          type: 'categories',
          completed: true,
          gmbSynced: true,
          categories: categoriesPayload,
          completedAt: new Date().toISOString()
        });
      }, 1500);

    } catch (err) {
      logger.error('[CategoryPopup] Error updating categories:', err);
      setError(err.message || 'Failed to update categories');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <Box>
        <HeaderContainer>
          <IconContainer>
            <CheckCircle sx={{ fontSize: 40, color: 'white' }} />
          </IconContainer>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Categories Updated!
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
            Your business categories have been synced with Google.
          </Typography>
        </HeaderContainer>
      </Box>
    );
  }

  return (
    <Box>
      <HeaderContainer>
        <IconContainer>
          <Category sx={{ fontSize: 36, color: 'white' }} />
        </IconContainer>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Manage Categories
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
          Update your business categories to improve discoverability
        </Typography>
      </HeaderContainer>

      <ContentContainer>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>
            Primary Category *
          </Typography>
          <Autocomplete
            options={categoryOptions}
            value={primaryCategory}
            inputValue={primaryInputValue}
            onChange={handlePrimarySelect}
            onInputChange={handlePrimaryInputChange}
            getOptionLabel={(option) => option?.label || ''}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            loading={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search for a category..."
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading && <CircularProgress size={16} sx={{ mr: 1 }} />}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>
            Additional Categories
          </Typography>
          
          {additionalCategories.map((entry, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Autocomplete
                sx={{ flex: 1 }}
                options={categoryOptions}
                value={entry.selected}
                inputValue={entry.inputValue}
                onChange={handleAdditionalSelect(index)}
                onInputChange={handleAdditionalInputChange(index)}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                loading={loading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={`Additional category ${index + 1}`}
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading && <CircularProgress size={16} sx={{ mr: 1 }} />}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />
              <IconButton onClick={() => handleRemoveCategory(index)} sx={{ color: '#ef4444' }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}

          <Button
            startIcon={<AddIcon />}
            onClick={handleAddCategory}
            sx={{
              textTransform: 'none',
              color: '#0B91D6',
              fontSize: '14px',
              fontWeight: 500,
              mt: 1
            }}
          >
            Add another category
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
          <ActionButton variant="secondary" onClick={onBack} startIcon={<ArrowBack />}>
            Back
          </ActionButton>
          <ActionButton
            variant="primary"
            onClick={handleComplete}
            disabled={submitting || !primaryCategory?.id}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
          >
            {submitting ? 'Updating...' : 'Complete Task'}
          </ActionButton>
        </Box>
      </ContentContainer>
    </Box>
  );
};

export default CategoryManagementPopup;
